import { ScreenHelp } from "@/components/Help";
import { Card, SectionTitle, tdBase, tdLabelBase, thBase } from "@/components/ui";
import { type PartPlan, type ProjectionResult, multiYearPnL } from "@/lib/cost/projection";
import { fmt, fmtComp } from "@/lib/data";
import { useIsMobile } from "@/lib/useViewport";
import type { CSSProperties } from "react";

// 다년 손익 추정 (이슈 #11) — 부품×연도 매출/손익/손익율 매트릭스.
//
// framing: 내부원가(SOP) 고정, 견적가 CR 후 동결, 손익율은 연차마다 하락.
// 계산 로직은 전부 `@/lib/cost/projection` 에 있고 이 컴포넌트는 입력(PartPlan)을
// 받아 렌더만 한다(계산 금지). 입력 소스가 없으면 빈 상태를 표시한다.
// recharts/react-router/react-query 미사용 — plain table + CSS bar (레포 컨벤션).

// 부품 계획 입력 — 아직 백엔드/입력 화면 연동이 없어 항상 null.
// 연동 시 이 함수를 hooks/api 호출로 교체한다. null 이면 빈 상태 메시지를 표시한다.
interface ProjectionInput {
  parts: PartPlan[];
  years: number[];
}
function loadProjectionInput(): ProjectionInput | null {
  return null;
}

const COST_PROJECTION_HELP = [
  "부품×연도 매트릭스로 다년 매출 · 손익액 · 손익율을 추정합니다.",
  "내부원가는 SOP 시점 값으로 고정하고, 견적가는 연도별 CR 적용 후 Y+3 값으로 동결합니다.",
  "매출 = 견적가 × 부품수량, 손익액 = (견적가 − SOP 내부원가) × 수량, 손익율 = 손익액 / 매출.",
  "손익율 바는 0–10% 스케일 — 녹색일수록 양호, 0 근처는 회색 계열, 음수는 빨강입니다.",
];

const thStyle: CSSProperties = thBase;
const tdStyle: CSSProperties = tdBase;
const tdLabel: CSSProperties = tdLabelBase;

const pctStr = (r: number): string => `${(r * 100).toFixed(1)}%`;

// 손익율 색상 — 높을수록 녹색, 0 근처는 회색, 음수는 빨강.
function rateColor(r: number): string {
  if (r <= 0) return "var(--num-negative)";
  if (r >= 0.08) return "var(--num-positive)";
  if (r >= 0.04) return "#9acd6a"; // amber-green
  return "#d6a14a";
}

// 손익율 CSS bar (0..10% 스케일).
const RateBar = ({ rate }: { rate: number }) => {
  const w = Math.max(0, Math.min(1, rate / 0.1)) * 100;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
      <div
        style={{
          width: 64,
          height: 6,
          background: "var(--bg-card)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div style={{ width: `${w}%`, height: "100%", background: rateColor(rate) }} />
      </div>
      <span style={{ minWidth: 44, color: rateColor(rate), fontWeight: 700 }}>{pctStr(rate)}</span>
    </div>
  );
};

const MatrixTable = ({
  title,
  rowLabel,
  result,
  pick,
  bars,
  signed,
}: {
  title: string;
  rowLabel: string;
  result: ProjectionResult;
  pick: (revenue: number, profit: number, rate: number) => number;
  bars?: boolean;
  signed?: boolean; // 손익액처럼 음수가 의미 있는 값 — 음수만 빨강으로 표시
}) => (
  <Card pad={0}>
    <div style={{ padding: "18px 24px 4px" }}>
      <SectionTitle sub={rowLabel}>{title}</SectionTitle>
    </div>
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>부품</th>
            {result.years.map((y) => (
              <th key={y} style={thStyle}>
                {y}
              </th>
            ))}
            <th style={{ ...thStyle, color: "var(--num-positive)" }}>계</th>
          </tr>
        </thead>
        <tbody>
          {result.parts.map((p) => {
            const total = pick(p.totalRevenue, p.totalProfit, p.totalProfitRate);
            return (
              <tr key={p.name}>
                <td style={tdLabel}>{p.name}</td>
                {p.years.map((y) => {
                  const v = pick(y.revenue, y.profit, y.profitRate);
                  return (
                    <td
                      key={y.year}
                      style={{
                        ...tdStyle,
                        color: signed && v < 0 ? "var(--num-negative)" : tdStyle.color,
                      }}
                    >
                      {bars ? <RateBar rate={v} /> : fmtComp(v)}
                    </td>
                  );
                })}
                <td
                  style={{
                    ...tdStyle,
                    color: signed && total < 0 ? "var(--num-negative)" : "var(--text-base)",
                    fontWeight: 700,
                  }}
                >
                  {bars ? <RateBar rate={total} /> : fmtComp(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);

const KpiCard = ({ label, value, sub }: { label: string; value: string; sub: string }) => (
  <Card>
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        color: "var(--text-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 800,
        color: "var(--text-base)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 4 }}>{sub}</div>
  </Card>
);

export const CostProjection = ({ onToast: _onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  const input = loadProjectionInput();

  if (!input || input.parts.length === 0) {
    return (
      <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 28 }}>
        <SectionTitle sub="내부원가(SOP) 고정, 견적가 CR 후 동결(Y+3) — 부품×연도 매출/손익/손익율 매트릭스">
          다년 손익 추정
        </SectionTitle>
        <ScreenHelp items={COST_PROJECTION_HELP} />
        <Card>
          <div
            style={{
              padding: "48px 0",
              textAlign: "center",
              color: "var(--text-faint)",
              fontSize: 14,
            }}
          >
            표시할 내용이 없습니다 — 등록된 부품 계획 데이터가 없습니다.
          </div>
        </Card>
      </div>
    );
  }

  const { parts, years } = input;
  const result = multiYearPnL(parts, { years });

  return (
    <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 28 }}>
      <SectionTitle sub="내부원가(SOP) 고정, 견적가 CR 후 동결(Y+3), 손익율 하락">
        다년 손익 추정 ({years[0]}–{years[years.length - 1]})
      </SectionTitle>

      <ScreenHelp items={COST_PROJECTION_HELP} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
          gap: 28,
        }}
      >
        <KpiCard
          label="총 매출"
          value={`${fmt(result.totalRevenue)} 원`}
          sub={`${fmtComp(result.totalRevenue)} · ${result.parts.length}개 부품 × ${years.length}년`}
        />
        <KpiCard
          label="총 손익"
          value={`${fmt(result.totalProfit)} 원`}
          sub={fmtComp(result.totalProfit)}
        />
        <KpiCard label="총 손익율" value={pctStr(result.totalProfitRate)} sub="총손익 / 총매출" />
      </div>

      <MatrixTable
        title="매출 (견적가 × 부품수량)"
        rowLabel="연도별 매출, 단위 압축 표기 · 계 = Σ over years"
        result={result}
        pick={(revenue) => revenue}
      />
      <MatrixTable
        title="손익액 ((견적가 − SOP 내부원가) × 수량)"
        rowLabel="연도별 손익액 · 견적가 동결 후 일정"
        result={result}
        pick={(_revenue, profit) => profit}
        signed
      />
      <MatrixTable
        title="손익율 (손익액 / 매출)"
        rowLabel="연차마다 하락 · 0–10% 스케일 바"
        result={result}
        pick={(_revenue, _profit, rate) => rate}
        bars
      />
    </div>
  );
};
